<template>
  <TabGroup
    v-if="tabs.length > 1"
    :selected-index="selected_tab_index"
    class="relative w-[105.5%] -mt-[23px] -ml-[2.75%] rounded-t-lg bg-gray-100 transition ease-in-out focus:outline-none duration-side-menu-transition-duration"
    as="div"
    @change="handleSelectTab">
    <TabList
      id="tabs-container"
      class="relative flex items-start justify-between w-full h-16 pr-2 ml-0 space-x-0 overflow-auto overflow-y-visible rounded-tr-md no-scrollbar focus:outline-none">
      <Tab v-for="(tab, i) in tabs"
           :id="i"
           :key="i"
           as="template"
           class="w-1/2">
        <div class="relative w-full outline-none ring-0 tex">
          <div
            :class="[selected_tab_index == i ? '!text-blue text-[15px] font-normal' : 'font-normal']"
            class="sm:text-lg text-center py-1 w-full transform translate-y-[3px] text-common-grey transition ease-in-out duration-300 rounded-sm relative focus:outline-none inline-flex cursor-pointer">
            <div
              :class="{
                'rounded-l-md': !i,
                'rounded-r-md': i == tabs.length - 1
              }"
              class="flex items-center w-full h-12 px-8 py-4 text-center ml-28 whitespace-nowrap">
              <List v-if="!i" :color="!selected_tab_index ? '#3FACD9' : '#899399'" class="w-6 h-6 mr-4" />
              <TrendingUp v-else class="w-6 h-6 mr-4" />
              {{ tab.name }}
            </div>

            <div v-if="selected_tab_index == i" class="tab-element absolute -z-10 bottom-0 h-2 ml-2 w-[100%] ]" />
          </div>
        </div>
      </Tab>
    </TabList>
  </TabGroup>

  <div v-if="!selected_tab_index && tabs.length > 1">
    <div class="m-6 border">
      <h3 class="p-2 mb-4 text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
        <span class="text-md text-common-grey">{{ records.count }} {{ $t("total") }} ({{ selected_records.length }} {{ $t("selected") }})</span>
      </h3>

      <!-- <input v-model="search_term" label="" :class="['w-full']" class="block w-full px-3 py-2 mb-4 placeholder-gray-400 transition-all ease-in border border-gray-300 rounded-md shadow-sm appearance-none dark:bg-dark-box-background dark:text-white dark:border-dark-divider-border focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm" type="text" placeholder="Search records" /> -->

      <div
        v-for="(record, i) in filtered_records"
        :key="record.id"
        :class="{ 'border-b': i < filtered_records.length - 1 }"
        class="pb-2 mb-4">
        <div class="flex items-center text-light-body-text dark:text-dark-body-text">
          <input
            :checked="selected_records.includes(record.id)"
            type="checkbox"
            class="ml-2 mr-2 mt-2 form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-common-grey w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration"
            @input="(e) => handleSelectRecord(e, record)">

          <div>
            <div :class="['font-normal ml-3 block truncate mt-1 cursor-pointer']" @click="$utils.Record(record).open()">
              {{
                Record.from(record)?.name
                  ? Record.from(record).name
                  : record.values[
                    $store.state.integration.platform_metric_mappings.find((m) => m.option == "name" && m.object_type == record.type)
                      ?.reference_name
                  ]
              }}
            </div>
          </div>
        </div>
      </div>
      <button v-if="records.next" class="cancel-btn" @click="handleLoadMoreRecords">{{ $t("Load more") }}</button>

      <!-- <div v-if="!filtered_company_contacts.length" class="text-sm text-common-grey">No contacts matching search query</div> -->
    </div>
  </div>

  <div v-else>
    <div class="scale-125 w-[75%] ml-[13%] mt-6">
      <NestedMetricView
        :graph-label-value="graphLabelValue"
        :truncate-by="truncateBy"
        :rollup-metric="metric"
        :metric="graphMetric"
        :for-graph="true"
        :filters="filters" />
    </div>
  </div>
  <div class="mt-20 text-center">
    <button
      type="button"
      class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
      @click="$emit('cancel')">
      {{ $t("Close") }}
    </button>
  </div>
</template>
<script>
import { Tab, TabGroup, TabList } from "@headlessui/vue"

import dataService from "@/services/data.service"
import { Record } from "@/models/record"

import NestedMetricView from "./NestedMetricView.vue"
import { TrendingUp, List } from "lucide-vue-next"

export default {
  components: {
    Tab,
    TabGroup,
    TabList,
    NestedMetricView,
    TrendingUp,
    List
  },
  props: {
    graphLabelValue: null,
    graphMetric: null,
    metric: null,
    filter: null,
    truncateBy: null
  },
  emits: ["cancel"],
  data() {
    return {
      Record,
      records: { count: 0, next: null, results: [] },
      filtered_records: [],
      backup_records: [],
      backup_records_obj: null,
      selected_records: [],
      search_term: "",
      selected_tab_index: 0,
      tabs: [],
      filters: null
    }
  },

  watch: {
    search_term() {
      this.$utils.debounce(() => {
        const metric_name = this.getMetricRefName(this.graphMetric)
        if (this.search_term) {
          const filter_array = [
            this.graphMetric.reference_name,
            "==",
            `'${this.graphLabelValue}'`,
            "and",
            `${metric_name}`,
            "in",
            `'${this.search_term}'`
          ]
          if (this.filter) {
            filter_array.push("and")
            filter_array.push(this.filter)
          }
          const filters = {
            filter: filter_array.join(" "),
            object_types: [this.$utils.getMetricByReferenceName(this.graphMetric.reference_name).object_type]
          }

          dataService
            .filterRecords(filters)
            .then((res) => {
              if (!this.backup_records_obj) {
                this.backup_records_obj = this.records
              }
              if (!this.backup_records.length) {
                this.backup_records = this.filtered_records
              }
              this.records = res.data

              this.filtered_records = res.data.results
            })
            .catch(() => {})
        } else {
          this.filtered_records = this.backup_records
          this.records = this.backup_records_obj
          this.backup_records = []
          this.backup_records_obj = null
        }
      }, 600)
    }
  },
  created() {
    this.tabs = []
    if (this.graphMetric) {
      this.tabs.push({ name: this.$t("List of all records") })
    }

    if (this.$utils.getMetricByReferenceName(this.metric.reference_name)) {
      this.tabs.push({ name: this.$t("View Graph") })
    }

    let filter_array = []
    if (this.graphMetric) {
      if (this.graphMetric.data_type == "Json") {
        filter_array = [this.graphMetric.reference_name, "in", `'${this.graphLabelValue}'`]
      } else {
        filter_array = [this.graphMetric.reference_name, "==", `'${this.graphLabelValue}'`]
      }
    }
    if (this.filter) {
      if (this.graphMetric) {
        filter_array.push("and")
      }
      filter_array.push(this.filter)
    }
    const filters = {
      filter: filter_array.join(" "),
      object_types: [
        this.$utils.getMetricByReferenceName(this.graphMetric ? this.graphMetric.reference_name : this.metric.reference_name).object_type
      ]
    }

    this.filters = filters

    dataService
      .filterRecords(filters, this.truncateBy)
      .then((res) => {
        this.records = res.data
        this.filtered_records = res.data.results
      })
      .catch(() => {})
  },
  methods: {
    handleSelectTab(e) {
      this.selected_tab_index = e
    },
    getMetricRefName(record) {
      if (record.object_type == "company") {
        return this.$store.state.integration.metrics_mapping.company_name_metric
      } else if (record.object_type == "opportunity") {
        return this.$store.state.integration.metrics_mapping.opportunity_name_metric
      } else {
        return this.$store.state.integration.platform_metric_mappings.find((m) => m.option == "name" && m.object_type == record.object_type)
          .reference_name
      }
    },
    handleSelectRecord(e, record) {
      if (e.target.checked) {
        this.selected_records.push(record.id)
      } else {
        const index = this.selected_records.findIndex((r) => r === record.id)
        this.selected_records.splice(index, 1)
      }
    },
    handleLoadMoreRecords() {
      const metric_name = this.getMetricRefName(this.graphMetric)
      const filter_array_1 = [
        this.graphMetric.reference_name,
        "==",
        `'${this.graphLabelValue}'`,
        "and",
        `${metric_name}`,
        "in",
        `'${this.search_term}'`
      ]
      const filter_array_2 = [this.graphMetric.reference_name, "==", `'${this.graphLabelValue}'`]
      if (this.filter) {
        filter_array_1.push("and")
        filter_array_1.push(this.filter)
        filter_array_2.push("and")
        filter_array_2.push(this.filter)
      }
      const filters = {
        filter: this.search_term ? filter_array_1.join(" ") : filter_array_2.join(" "),
        object_types: [this.$utils.getMetricByReferenceName(this.graphMetric.reference_name).object_type]
      }
      dataService
        .getRecordsByUrl(this.records?.next, {
          filter: filters.filter,
          object_types: [this.$utils.getMetricByReferenceName(this.graphMetric.reference_name).object_type]
        })
        .then((res) => {
          this.records = res.data
          this.filtered_records = this.filtered_records.concat(this.records.results)
        })
    }
  }
}
</script>

<style scoped>
.tab-element::after {
  content: "";
  position: absolute;

  bottom: 0;
  left: -3px;
  width: 100%;
  box-shadow: 0 0 8px 1px;

  box-shadow: 1px 4px 1px 1px #2da7d7;

  z-index: -1;
}
</style>
